import React from 'react'
import * as Announce from '@radix-ui/react-announce'
import { styled, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import { Dict } from '@grupoboticario/flora-react-utils'
import { Box } from '@grupoboticario/flora-react-box'
import { Text } from '@grupoboticario/flora-react-text'
import {
  ThumbUpOutlineIcon,
  CrossCircleIcon,
  ExclamationMarkChatBubbleIcon,
  PersonCircleIcon,
} from '@grupoboticario/flora-react-icons'
import { motion } from 'framer-motion'

import { useMeasure } from '@grupoboticario/flora-react-hooks'

export const StyledToastButton = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  appearance: 'none',
  background: 'transparent',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  padding: '0 $2',
  color: '$actionableAlt',
  fontSize: '$bodySmall',
  lineHeight: '16px',

  transition: `
  background 150ms ease,
    box-shadow 150ms ease-in-out,
    color 150ms ease
`,

  '&::before, &::after': {
    boxSizing: 'border-box',
  },

  '&:active': {
    opacity: '0.8',
  },

  '&:focus': {
    boxShadow: '$outline',
  },
})

export const StyledToast = styled('div', {
  boxSizing: 'border-box',
  color: '$nonInteractiveAltPredominant',
  borderRadius: '$small',
  boxShadow: '$downFar',
  display: 'flex',
  flexDirection: 'row',
  gap: '$2',
  userSelect: 'none',
  position: 'relative',
  padding: '$3',

  '& > svg': {
    marginRight: '$2',
    width: '16px',
    height: 'auto',
  },
  '& svg path': {
    fill: '$nonInteractiveAltPredominant',
  },
  variants: {
    variant: {
      neutral: {
        background: '$backgroundAltSecondary',
        [`& ${StyledToastButton}`]: {
          color: '$colors$linkAlt',
        },
      },
      success: {
        background: '$success-standard',
      },
      alert: {
        background: '$alert-standard',
      },
      error: {
        background: '$error-standard',
      },
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
})

const StyledToastTimeBar = styled('span', {
  width: '100%',
  height: '3px',
  position: 'absolute',
  bottom: 0,
  left: 0,
  background: '$disabledBackground',
  borderRadius: '0 0 $small $small',
  overflow: 'hidden',
  '& > span': {
    height: '3px',
    display: 'block',
  },
  variants: {
    variant: {
      success: {
        '& > span': {
          background: '$success-alt',
        },
      },
      neutral: {
        '& > span': {
          background: '$disabledAltBackground',
        },
      },
      alert: {
        '& > span': {
          background: '$alert-alt',
        },
      },
      error: {
        '& > span': {
          background: '$error-alt',
        },
      },
    },
  },
})

const ToastIcons = {
  success: <ThumbUpOutlineIcon size={20} />,
  error: <CrossCircleIcon size={20} />,
  alert: <ExclamationMarkChatBubbleIcon size={20} />,
  neutral: <PersonCircleIcon size={20} />,
} as Dict

export type ToastProps = FloraVariants<typeof StyledToast> & {
  Icon?: React.ReactNode | Element
  button?: string
  buttons?: string[]
  duration?: number
  /**
   * Call a single callback
   */
  callback?: () => {}
  /**
   * Call callbacks passed by array
   */
  callbacks?: Function[]
  remove?: () => void
  /**
   * Option to close the Toast when user clicks any button
   * Default is true
   */
  closeWhenClick?: boolean
  /**
   * If true, the Toast icon will visible
   * Default is true
   */
  showIcon?: boolean
}

/**
 * @component
 * @name Toast
 * @description Toast component
 * @tutorial https://flora.gb.tech/docs/components/core/toast
 */
export const Toast = forwardRef<'div', ToastProps>(
  (
    {
      buttons = [],
      button,
      duration = 5000,
      variant = 'neutral',
      Icon: CustomIcon,
      children,
      callback = () => {},
      callbacks = [() => {}, () => {}],
      closeWhenClick = true,
      remove = () => {},
      showIcon,
      ...rest
    },
    forwardedRef,
  ) => {
    const CurrentIcon = ToastIcons[variant as string] as unknown as (
      props?: {} | undefined,
    ) => JSX.Element

    const toastIcon = showIcon ? CustomIcon || CurrentIcon : null

    return (
      <StyledToast
        ref={forwardedRef}
        data-flora="toast"
        variant={variant}
        css={{
          ...(!!buttons?.length && {
            flexDirection: 'column',
            flexWrap: 'wrap',
          }),
        }}
        {...rest}
      >
        <>
          <Box css={{ display: 'flex', gap: '$2' }}>
            {toastIcon}
            <Announce.Root asChild>
              <Text
                as="span"
                css={{
                  fontSize: '$bodySmall',
                }}
              >
                {children}
              </Text>
            </Announce.Root>
            {button && !buttons?.length && (
              <StyledToastButton
                onClick={() => {
                  if (closeWhenClick && remove) {
                    remove()
                  }
                  if (typeof callback === 'function') {
                    callback()
                  }
                }}
                data-flora="toast-action-1"
              >
                {button}
              </StyledToastButton>
            )}
          </Box>

          {!!buttons.length && (
            <Box
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {(buttons as string[]).map((currentButton, index) => (
                <StyledToastButton
                  key={currentButton}
                  onClick={() => {
                    if (closeWhenClick && remove) {
                      remove()
                    }
                    if (typeof callbacks[index] === 'function') {
                      callbacks[index]()
                    }
                  }}
                  data-flora={`toast-action-${index + 1}`}
                >
                  {currentButton}
                </StyledToastButton>
              ))}
            </Box>
          )}

          {variant && (
            <StyledToastTimeBar variant={variant as 'neutral' | 'success' | 'alert' | 'error'}>
              <motion.span
                initial={{
                  width: '0%',
                }}
                animate={{
                  width: '100%',
                }}
                transition={{
                  duration: duration / 1000,
                }}
              />
            </StyledToastTimeBar>
          )}
        </>
      </StyledToast>
    )
  },
)

export const ToastWrapper = ({
  children,
  invert = false,
}: {
  children?: React.ReactNode
  invert?: boolean
}) => {
  const [measureRef, { height }] = useMeasure()

  const initalY = invert ? 200 : -200
  const exitY = invert ? -height : 0

  return (
    <motion.div
      initial={{ y: initalY, height: 0, scale: 0.9 }}
      animate={{
        scale: 1,
        height,
        y: 0,
      }}
      exit={{ opacity: 0, scale: 0.9, height: 0, y: exitY }}
      transition={{
        duration: 0.3,
      }}
    >
      <Box ref={measureRef}>
        <Box
          css={{
            boxSizing: 'border-box',
            padding: '$1 $2',
          }}
        >
          {children}
        </Box>
      </Box>
    </motion.div>
  )
}
